/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/

/**
 * Helpers for picking an option path from a service request type's hierarchy
 * (e.g. Category > Asset > Manufacturer) on the New/Edit Service Request forms.
 *
 * A selection is kept as `{ [depth]: nodeUid }`. Options are "normalized nodes"
 * — `{ uid, parentUid, depth, name, isActive }` — so options restored from a
 * saved `hierarchySelections` snapshot (which only know uids) can sit alongside
 * nodes loaded from GET /service-request-types/:uid/hierarchy.
 *
 * Every level is optional, but a level can only be picked once the level above
 * it is — mirrors ServiceRequestTypesService.resolveHierarchySelection.
 */

/** Maps hierarchy API nodes (parent linked by internal id) to normalized nodes. */
export const normalizeHierarchyNodes = (nodes) => {
  const uidById = new Map((nodes || []).map((n) => [n.id, n.uid]));
  return (nodes || []).map((n) => ({
    uid: n.uid,
    parentUid: n.parentId ? uidById.get(n.parentId) || null : null,
    depth: n.depth,
    name: n.name,
    isActive: n.isActive,
  }));
};

/**
 * Normalized nodes for a saved snapshot's entries that are missing from the
 * currently loaded (active) options — e.g. deactivated since — so the saved
 * path still displays and can be kept as-is.
 */
export const snapshotOptionsNotIn = (selections, nodes) => {
  const known = new Set((nodes || []).map((n) => n.uid));
  const sorted = [...(selections || [])].sort((a, b) => a.depth - b.depth);
  return sorted
    .map((s, i) => ({
      uid: s.nodeUid,
      parentUid: i > 0 ? sorted[i - 1].nodeUid : null,
      depth: s.depth,
      name: s.nodeName,
      isActive: false,
    }))
    .filter((n) => !known.has(n.uid));
};

/** `[{ depth, nodeUid }]` snapshot → `{ [depth]: nodeUid }` form value. */
export const selectionsToValue = (selections) =>
  (selections || []).reduce((acc, s) => ({ ...acc, [s.depth]: s.nodeUid }), {});

export const getOptionsAtDepth = (nodes, depth, parentUid) =>
  (nodes || []).filter((n) => n.depth === depth && (depth === 0 ? !n.parentUid : n.parentUid === parentUid));

/** True when the type has levels and at least one option to pick from. */
export const hasSelectableHierarchy = (levels, nodes) =>
  (levels || []).length > 0 && getOptionsAtDepth(nodes, 0, null).length > 0;

/** uid of the deepest picked option, or null when nothing is picked. */
export const getLeafUid = (value) => {
  const depths = Object.keys(value || {})
    .map(Number)
    .filter((d) => value[d]);
  return depths.length ? value[Math.max(...depths)] : null;
};

/** Sets one level and clears every deeper level (they depend on it). */
export const setHierarchyLevel = (value, depth, nodeUid) => {
  const next = {};
  Object.keys(value || {})
    .map(Number)
    .filter((d) => d < depth)
    .forEach((d) => { next[d] = value[d]; });
  if (nodeUid) next[depth] = nodeUid;
  return next;
};
